"use client";

import type React from "react";
import { Label } from "@/components/ui/label";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface BigSwitchProps {
    options: {
        value: string;
        icon?: React.FC<React.SVGProps<SVGSVGElement>>;
        label: any;
        sublabel?: any;
        disabled?: boolean;
    }[];
    value: string;
    onValueChange: (value: string) => void;
    defaultValue?: number;
}

const BigSwitchMotion: React.FC<BigSwitchProps> = ({ options, value, onValueChange, defaultValue = 0 }) => {
    // Find the selected option index
    const selectedIndex = options.findIndex((option) => option.value === value);

    // If selectedIndex is -1 (not found), use the defaultValue index instead
    const activeIndex = selectedIndex === -1 ? defaultValue : selectedIndex;

    // Set initial position to prevent jumping
    const [isInitialized, setIsInitialized] = useState(false);

    // Ensure the first option is selected by default when options change
    useEffect(() => {
        // If current value is not in options, select the default
        if (!options.some(option => option.value === value) && options.length > 0) {
            const fallbackValue = options[defaultValue]?.value ?? options[0].value;
            onValueChange(fallbackValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(true);
        }
    }, []);

    return (
        <div className="relative">
            <RadioGroupPrimitive.Root
                className="gap-4 flex flex-row bg-input border border-muted rounded-full p-1 h-fit w-full"
                value={value}
                onValueChange={onValueChange}
                defaultValue={options[defaultValue]?.value}
            >
                {/* Background slider */}
                <motion.div
                    className="absolute assented rounded-full z-0"
                    initial={{
                        left: `calc(${activeIndex * (100 / options.length)}% + 4px)`,
                        right: `calc(${100 - (activeIndex + 1) * (100 / options.length)}% + 4px)`,
                        top: "4px",
                        bottom: "4px",
                    }}
                    animate={{
                        left: `calc(${activeIndex * (100 / options.length)}% + 4px)`,
                        right: `calc(${100 - (activeIndex + 1) * (100 / options.length)}% + 4px)`,
                        top: "4px",
                        bottom: "4px",
                    }}
                    transition={{
                        type: "tween",
                        ease: "easeInOut",
                        duration: 0.3,
                    }}
                />

                {options.map((option, index) => (
                    <div
                        key={option.value}
                        className={`${
                            option.disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"
                        } relative flex items-center gap-2 rounded-full border border-transparent p-2 justify-center w-full h-10 outline-none transition-all duration-300 capitalize z-10`}
                        onClick={() => !option.disabled && onValueChange(option.value)}
                    >
                        {option.icon && (
                            <motion.div
                                animate={{
                                    scale: value === option.value ? 1.1 : 1,
                                }}
                                transition={{
                                    type: "tween",
                                    ease: "easeOut",
                                    duration: 0.2,
                                }}
                            >
                                <option.icon className="shrink-0" aria-hidden="true" />
                            </motion.div>
                        )}

                        {option.label && (
                            <RadioGroupPrimitive.Item
                                value={option.value}
                                id={`pool-switch-${option.value}`}
                                className="opacity-0 order-1 after:absolute after:inset-0 size-4 rounded-full border absolute !font-light text-accent"
                                disabled={option.disabled}
                            />
                        )}

                        {option.label && (
                            <motion.div className="grid gap-2">
                                <Label
                                    htmlFor={`pool-switch-${option.value}`}
                                    className={`${value === option.value ? "!text-white" : ""} text-md items-center flex gap-2 text-accent pointer-events-none`}
                                >
                                    {option.label}
                                </Label>
                            </motion.div>
                        )}
                    </div>
                ))}
            </RadioGroupPrimitive.Root>
        </div>
    );
};

export default BigSwitchMotion;
